// SFO Delay Odds (decided Oct 26, rounds one and two): a loop screen of five pages for the day you fly or pick someone
// up. 1) the board: the next six parts of the day, each with the odds of a 15+ minute delay and the reason; 2) why and
// where: when the low clouds clear, the cloud base hour by hour, and the FAA's status at SFO's busiest routes; 3) the
// ledger: how the next block's odds were built, from the usual rate through each piece of evidence; 4) arrivals: how
// late arrivals run; 5) getting there: the drive, when to leave for a domestic or international
// landing, and BART (4 and 5 were one page until Oct 27). One color scale throughout: green under
// 25%, amber to 40%, red above. The odds are placeholders until the backtest (config/sfo.json).
import { registerDisplay } from '../navigation.mjs';
import { escapeHtml } from '../utils/string.mjs';
import FeatureDisplay from './feature-display.mjs';
import { data, fetchedAt } from './snapshot.mjs';
import {
	asOf, time, weekday, isToday, dayKey,
} from './format.mjs';

const { sfo, clock } = window.RHL;
const HOUR = 3600000;
const DAY = 24 * HOUR;
const CEILING_COVERS = new Set(['BKN', 'OVC', 'OVX']);

// --- local time ------------------------------------------------------------------------------------------------------
const offsetHours = (t) => {
	const name = new Intl.DateTimeFormat('en-US', { timeZone: clock.timeZone, timeZoneName: 'shortOffset' }).formatToParts(t).find((p) => p.type === 'timeZoneName')?.value ?? 'GMT-8';
	const m = name.match(/GMT([+-]\d+)(?::(\d+))?/);
	return m ? Number(m[1]) + Math.sign(Number(m[1])) * (Number(m[2] ?? 0) / 60) : 0;
};
// the instant a local day's hour begins
const at = (key, hour) => {
	const guess = Date.parse(`${key}T00:00:00Z`) + hour * HOUR;
	return guess - offsetHours(new Date(guess)) * HOUR;
};
const localHour = (t) => Number(new Intl.DateTimeFormat('en-US', { timeZone: clock.timeZone, hour: 'numeric', hourCycle: 'h23' }).format(t));

// --- the forecast, hour by hour -----------------------------------------------------------------------------------
const ceilingOf = (p) => {
	const bases = (p.clouds ?? []).filter((c) => CEILING_COVERS.has(c.cover) && c.base !== null).map((c) => c.base);
	if (p.vertVis !== null && p.vertVis !== undefined) bases.push(p.vertVis);
	return bases.length ? Math.min(...bases) : null;
};
// conditions at an instant: the main forecast period, worsened by any TEMPO or PROB group that covers it
const conditionsAt = (weather, t) => {
	const covering = (weather?.periods ?? []).filter((p) => new Date(p.from) <= t && t < new Date(p.to));
	const main = covering.filter((p) => !['TEMPO', 'PROB'].includes(p.change)).at(-1);
	if (!main) return null;
	const temps = covering.filter((p) => ['TEMPO', 'PROB'].includes(p.change));
	const ceilings = [main, ...temps].map(ceilingOf).filter((c) => c !== null);
	const vis = [main, ...temps].map((p) => p.visib).filter((v) => v !== null);
	return {
		ceiling: ceilings.length ? Math.min(...ceilings) : null,
		visib: vis.length ? Math.min(...vis) : null,
		wdir: main.wdir,
		wspd: main.wspd ?? 0,
		gust: Math.max(main.wgst ?? 0, ...temps.map((p) => p.wgst ?? 0)),
		wx: [main, ...temps].map((p) => p.wx).filter(Boolean).join(' '),
	};
};

// --- the odds ---------------------------------------------------------------------------------------------------------
const toOdds = (p) => p / (1 - p);
const toP = (o) => o / (1 + o);
const level = (p) => (p >= sfo.levels.red ? 'high' : p >= sfo.levels.amber ? 'mid' : 'low');
const pct = (p) => `${Math.round(p * 100)}%`;
const feet = (ft) => `${ft.toLocaleString('en-US')} ft`;

const sfoEvents = () => (data('faa') ?? []).find((a) => a.airport === sfo.faaId) ?? null;

// one part of the day: its conditions (the worst hour), the evidence, the odds
const scoreBlock = (block, key, weather, now) => {
	const start = Math.max(at(key, block.from), now.getTime());
	const end = at(key, block.to);
	const hours = [];
	for (let t = start; t < end; t += HOUR) {
		const c = conditionsAt(weather, new Date(t));
		if (c) hours.push(c);
	}
	const ev = sfo.evidence;
	const factors = [];
	if (hours.length) {
		const ceilings = hours.map((h) => h.ceiling ?? 99999);
		const low = Math.min(...ceilings);
		const band = ev.ceiling.find((b) => low < b.below) ?? ev.ceiling.at(-1);
		factors.push({ why: low < sfo.clearCeiling ? `Low clouds, ${feet(low)}` : band.why, x: band.x });
		const vis = Math.min(...hours.map((h) => h.visib ?? 10));
		if (vis < ev.visibility.below) factors.push({ why: ev.visibility.why, x: ev.visibility.x });
		const gust = Math.max(...hours.map((h) => h.gust ?? 0));
		const g = ev.gust.find((b) => gust >= b.atLeast);
		if (g) factors.push({ why: `${g.why}, ${gust} kt`, x: g.x });
		const se = hours.some((h) => h.wdir !== null && h.wdir >= ev.southeast.from && h.wdir <= ev.southeast.to && h.wspd >= ev.southeast.atLeast);
		if (se) factors.push({ why: ev.southeast.why, x: ev.southeast.x });
		if (hours.some((h) => new RegExp(ev.weather.match).test(h.wx))) factors.push({ why: ev.weather.why, x: ev.weather.x });
	}
	let p = toP(factors.reduce((o, f) => o * f.x, toOdds(block.usual)));
	// the FAA's own programs at SFO, while they overlap this block, set a floor
	const faa = sfoEvents();
	let program = null;
	if (faa?.program) {
		const ps = faa.program.starts ? new Date(faa.program.starts).getTime() : 0;
		const pe = faa.program.ends ? new Date(faa.program.ends).getTime() : Infinity;
		if (ps < end && pe > start) {
			program = faa.program;
			p = Math.max(p, ev.program.floor);
		}
	}
	if (faa?.stop && start <= now.getTime() + HOUR) p = Math.max(p, ev.stop.floor);
	const reason = faa?.stop && start <= now.getTime() + HOUR ? 'FAA ground stop'
		: program ? `FAA program, avg ${program.avg} min`
			: !hours.length ? 'Beyond the forecast'
				: [...factors].sort((a, b) => b.x - a.x)[0]?.why ?? 'Clear';
	return {
		block, key, start, end, p, factors, program, covered: hours.length > 0, reason,
	};
};

// the next six parts of the day from now
const nextBlocks = (weather, now) => {
	const out = [];
	for (let d = 0; d < 3 && out.length < 6; d += 1) {
		const key = dayKey(now.getTime() + d * DAY);
		sfo.blocks.forEach((block) => {
			if (out.length < 6 && at(key, block.to) > now.getTime()) out.push(scoreBlock(block, key, weather, now));
		});
	}
	return out;
};

const dayName = (key, now) => {
	if (key === dayKey(now)) return 'TODAY';
	return weekday(new Date(`${key}T12:00:00`)).toUpperCase();
};
const chip = (lv) => `<span class="sfo-chip ${lv}">${{ low: 'LOW', mid: 'ELEV', high: 'HIGH' }[lv]}</span>`;
const meter = (p) => {
	const on = Math.round(p * 10);
	return `<div class="meter sfo-meter ${level(p)}">${Array.from({ length: 10 }, (_, i) => `<i class="${i < on ? 'on' : ''}"></i>`).join('')}</div>`;
};
const minutesText = (m) => (m >= 60 ? `${Math.floor(m / 60)} h${m % 60 ? ` ${m % 60} min` : ''}` : `${m} min`);
// the same, short enough for the big type and the FAA line ("1h 52m")
const minutesShort = (m) => (m >= 60 ? `${Math.floor(m / 60)}h${m % 60 ? ` ${m % 60}m` : ''}` : `${m} min`);

// the FAA's line for SFO: across the top of the board, at the foot of the arrivals page
const faaLine = (now, where = '') => {
	const cls = where ? ` ${where}` : '';
	const e = sfoEvents();
	if (e?.stop) return `<div class="sfo-faa${cls} high">FAA: ground stop at SFO${e.stop.ends ? ` until ${time(e.stop.ends)}` : ''}</div>`;
	if (e?.program) return `<div class="sfo-faa${cls} high">FAA program: SFO delays avg ${minutesShort(e.program.avg)}, max ${minutesShort(e.program.max)}${e.program.ends ? `, until ${isToday(e.program.ends, now) ? '' : `${weekday(e.program.ends)} `}${time(e.program.ends)}` : ''}</div>`;
	if (e?.arrivals || e?.departures) {
		const d = e.arrivals ?? e.departures;
		return `<div class="sfo-faa${cls} mid">FAA: ${e.arrivals ? 'arrival' : 'departure'} delays at SFO, ${d.min ?? '?'}-${d.max ?? '?'}${d.trend ? `, ${escapeHtml(d.trend.toLowerCase())}` : ''}</div>`;
	}
	return `<div class="sfo-faa${cls} low">FAA: no delays reported at SFO</div>`;
};

// a hub's status from the FAA's events
const hubStatus = (code) => {
	const e = (data('faa') ?? []).find((a) => a.airport === code);
	if (!e) return { lv: 'low', text: 'OK' };
	if (e.stop) return { lv: 'high', text: 'Stop' };
	if (e.program) return { lv: e.program.avg >= 45 ? 'high' : 'mid', text: `~${e.program.avg}m` };
	const d = e.departures ?? e.arrivals;
	const mins = [d?.min, d?.max].map((v) => String(v ?? '').match(/\d+/)?.[0]).filter(Boolean);
	return { lv: 'mid', text: mins.length ? `${mins.join('-')}m` : 'Delays' };
};

class SfoOdds extends FeatureDisplay {
	constructor(navId, elemId) {
		super(navId, elemId, 'SFO Delay Odds');
	}

	// eslint-disable-next-line class-methods-use-this
	hasData() {
		return !!data('sfoWeather')?.periods?.length;
	}

	// eslint-disable-next-line class-methods-use-this
	buildPages(now) {
		const weather = data('sfoWeather');
		const blocks = nextBlocks(weather, now);
		if (!blocks.length) return [];
		const forecastAt = weather.issued ? `forecast ${time(weather.issued)}` : 'no forecast';
		const faaAt = fetchedAt('faa') ? `FAA ${time(fetchedAt('faa'))}` : 'FAA status unavailable';
		const pages = [];

		// 1) the board
		let lastDay = null;
		const rows = blocks.map((b) => {
			const day = b.key === lastDay ? '' : dayName(b.key, now);
			lastDay = b.key;
			const lv = level(b.p);
			const known = b.covered || b.program;
			return `<div class="hdr">${day}</div><div>${b.block.name}</div>${known ? chip(lv) : '<span class="sfo-chip none">--</span>'}<div class="t-${known ? lv : 'none'} n">${known ? pct(b.p) : '--'}</div><div class="dim why">${escapeHtml(b.reason)}</div>`;
		}).join('');
		pages.push({
			seconds: 12,
			title: ['SFO Departures', 'Delay Odds'],
			html: `${faaLine(now)}<div class="sfo-board">${rows}</div>
				<div class="asof">Departures: odds of a 15+ min delay &middot; ${forecastAt}</div>`,
		});

		// 2) why and where
		const nowCeiling = weather.now ? ceilingOf(weather.now) : conditionsAt(weather, now)?.ceiling ?? null;
		let left;
		if (nowCeiling !== null && nowCeiling < sfo.clearCeiling) {
			let clears = null;
			for (let t = now.getTime() + HOUR; t < now.getTime() + 30 * HOUR; t += HOUR) {
				const c = conditionsAt(weather, new Date(t));
				if (!c) break;
				if (c.ceiling === null || c.ceiling >= sfo.clearCeiling) {
					clears = t; break;
				}
			}
			// Oct 27 (Jared: highlight paired landings): the thing that matters is whether SFO can land two at once
			const lv = nowCeiling < 1500 ? 'high' : 'mid';
			left = `<div class="hdr">Landings</div><div class="big t-${lv}">Single file</div><div class="dim">Low clouds at ${feet(nowCeiling)}; paired again ${clears ? `by ${time(clears)}` : 'not soon'}</div>`;
		} else {
			left = `<div class="hdr">Landings</div><div class="big t-low">Paired</div><div class="dim">Side by side on both runways${nowCeiling !== null ? `; clouds at ${feet(nowCeiling)}` : ''}</div>`;
		}
		const strip = Array.from({ length: 18 }, (_, i) => {
			const t = new Date(now.getTime() + i * HOUR);
			const c = conditionsAt(weather, t);
			const lv = !c ? 'none' : c.ceiling === null || c.ceiling >= sfo.clearCeiling ? 'low' : c.ceiling >= 1500 ? 'mid' : 'high';
			return `<i class="${lv}" title="${localHour(t)}:00"></i>`;
		}).join('');
		const hourLabel = (t) => {
			const h = localHour(t); return h === 0 ? 'mid' : h === 12 ? 'noon' : `${h % 12}${h < 12 ? 'a' : 'p'}`;
		};
		const stripLabels = [0, 6, 12, 17].map((i) => `<span>${i ? hourLabel(now.getTime() + i * HOUR) : 'now'}</span>`).join('');
		const tiles = sfo.hubs.map((code) => {
			const s = hubStatus(code);
			return `<div class="sfo-tile ${s.lv}"><span class="hdr">${code}</span> <span class="t-${s.lv}">${escapeHtml(s.text)}</span></div>`;
		}).join('');
		pages.push({
			seconds: 12,
			title: ['SFO', 'Why & Where'],
			html: `<div class="sfo-two">
					<div>${left}</div>
					<div><div class="hdr">Paired landings, next 18 hours</div><div class="sfo-strip">${strip}</div><div class="sfo-strip-axis">${stripLabels}</div>
					<div class="dim small">green paired, amber or red single file</div></div>
				</div>
				<div class="hdr gap">Where you're flying</div><div class="sfo-tiles">${tiles}</div>
				<div class="asof">${faaAt} &middot; ${forecastAt}</div>`,
		});

		// 3) the ledger, for the next block
		const next = blocks[0];
		const lv = level(next.p);
		const { usual } = next.block;
		const ledgerRows = [
			[`Usual ${next.block.name.toLowerCase()}`, pct(usual), level(usual)],
			...next.factors.map((f) => [f.why, `x${f.x.toFixed(1)}`, f.x > 1 ? (f.x >= 2 ? 'high' : 'mid') : 'low']),
			[next.program ? 'FAA program' : 'FAA programs', next.program ? `floor ${pct(sfo.evidence.program.floor)}` : 'none', next.program ? 'high' : 'low'],
		].map(([k, v, l]) => `<div>${escapeHtml(k)}</div><div class="t-${l} v">${v}</div>`).join('');
		pages.push({
			seconds: 12,
			title: ['SFO Departures', 'The Odds'],
			html: `<div class="sfo-two">
					<div><div class="hdr">${dayName(next.key, now) === 'TODAY' ? '' : `${dayName(next.key, now)} `}${next.block.name} departures</div>
						<div class="big t-${lv}">${pct(next.p)}</div><div class="dim">chance of 15+ min late</div>${meter(next.p)}</div>
					<div><div class="hdr">How we got there</div><div class="sfo-ledger">${ledgerRows}<div class="hdr">Now</div><div class="t-${lv} v">${pct(next.p)}</div></div></div>
				</div>
				<div class="asof">Odds are placeholders until the backtest &middot; ${forecastAt}</div>`,
		});

		// 4) arrivals, for a pickup from Russian Hill
		const e = sfoEvents();
		const current = blocks.find((b) => b.start <= now.getTime() + HOUR);
		let running;
		if (e?.program) running = { lv: 'high', big: `~${minutesShort(e.program.avg)}`, sub: `late, FAA program${e.program.ends ? ` until ${time(e.program.ends)}` : ''}` };
		else if (e?.arrivals) running = { lv: 'mid', big: `${e.arrivals.min ?? '?'}-${e.arrivals.max ?? '?'}`, sub: 'FAA: arrival delays' };
		else if (!current) running = { lv: 'none', big: 'Overnight', sub: `Next: ${blocks[0].block.name.toLowerCase()}, ${pct(blocks[0].p)} chance of 15+ min late` };
		else {
			const l = level(current.p);
			running = { lv: l, big: { low: 'On time', mid: 'Some late', high: 'Late' }[l], sub: `${pct(current.p)} chance of 15+ min, ${current.block.name.toLowerCase()}` };
		}
		const live = data('drive');
		const liveFresh = live && fetchedAt('drive') && now - new Date(fetchedAt('drive')) < 30 * 60000;
		const weekend = [0, 6].includes(new Date(`${dayKey(now)}T12:00:00Z`).getUTCDay());
		const typical = sfo.drive.typical[weekend ? 'weekend' : 'weekday'][localHour(now)];
		const driveMin = liveFresh ? live.minutes : typical;
		const driveLv = driveMin >= 40 ? 'high' : driveMin >= 30 ? 'mid' : 'low';
		const bart = data('bart');
		const bartNote = bart?.length ? '<span class="t-mid">advisory</span>' : `<span class="t-low">${sfo.drive.bartMinutes} min</span>`;
		// Oct 27 (Jared: break it up): arrivals on one page, getting there on the next
		const upcoming = blocks.filter((x) => x !== current).slice(0, 2)
			.map((x) => `<div>${dayName(x.key, now) === 'TODAY' ? '' : `${dayName(x.key, now)} `}${x.block.name}</div><div class="t-${x.covered || x.program ? level(x.p) : 'none'} v">${x.covered || x.program ? pct(x.p) : '--'}</div>`).join('');
		const faaDetail = e?.program
			? `<div>Avg delay</div><div class="t-high v">${minutesShort(e.program.avg)}</div><div>Longest</div><div class="t-high v">${minutesShort(e.program.max)}</div>`
			: e?.arrivals ? `<div>FAA delays</div><div class="t-mid v">${escapeHtml(`${e.arrivals.min ?? '?'}-${e.arrivals.max ?? '?'}`)}</div>` : '<div>FAA programs</div><div class="t-low v">none</div>';
		pages.push({
			seconds: 12,
			title: ['SFO Arrivals', 'Running Late'],
			// the FAA line sits at the foot here (Jared, Oct 27): the other pages open straight into their columns
			html: `<div class="sfo-two">
					<div><div class="hdr">Arrivals running</div><div class="big t-${running.lv}">${running.big}</div><div class="dim">${escapeHtml(running.sub)}</div></div>
					<div><div class="hdr">Odds of 15+ min late</div><div class="sfo-ledger">${upcoming}${faaDetail}</div></div>
				</div>
				${faaLine(now, 'foot')}
				<div class="asof">${faaAt} &middot; ${forecastAt}</div>`,
		});

		// getting there from Russian Hill: when to leave, counted from the landing, for a domestic and an international
		// arrival (the drive less the time from touchdown to the curb)
		// Oct 27 (Jared didn't follow "before/after landing"): a worked example instead. If their plane lands at 9:00,
		// leave home at 9:00 + the time to the curb - the drive
		const leave = (curb) => {
			const at = 9 * 60 + curb - driveMin;
			return `${Math.floor(at / 60) % 12 || 12}:${String(((at % 60) + 60) % 60).padStart(2, '0')}`;
		};

		pages.push({
			seconds: 12,
			title: ['SFO Arrivals', 'Getting There'],
			html: `<div class="sfo-two">
					<div><div class="hdr">Drive${liveFresh ? ', now' : ', typical'}</div><div class="big t-${driveLv}">${driveMin} min</div>
						<div class="dim">${liveFresh && live.typicalMinutes ? `usually ${live.typicalMinutes} min &middot; ` : ''}${liveFresh ? `${live.miles} mi` : 'no live traffic'}</div></div>
					<div><div class="hdr">Picking someone up?</div><div class="dim">If they land at 9:00, leave at</div><div class="sfo-ledger">
						<div>Domestic</div><div class="v">${leave(sfo.drive.curbMinutes.domestic)}</div>
						<div>International</div><div class="v">${leave(sfo.drive.curbMinutes.international)}</div>
					</div><div class="dim small">they reach the curb ~${sfo.drive.curbMinutes.domestic} min after landing, ~${sfo.drive.curbMinutes.international} after customs</div>
					<div class="hdr gap">Or BART</div><div class="sfo-ledger"><div>From Powell</div><div class="v">${bartNote}</div></div></div>
				</div>
				<div class="asof">${liveFresh ? `Mapbox traffic, ${asOf(fetchedAt('drive'), now)}` : `Typical ${weekend ? 'weekend' : 'weekday'}, this hour`} &middot; to the arrivals curb</div>`,
		});
		return pages;
	}
}

const display = new SfoOdds(32, 'sfo-odds');
display.makeLoopScreen();
registerDisplay(display);

// for the ticker: a line while the FAA has SFO under a program or a ground stop
const sfoTickerLine = () => {
	const e = sfoEvents();
	if (e?.stop) return `SFO ground stop${e.stop.ends ? ` until ${time(e.stop.ends)}` : ''}${e.stop.reason ? `, ${escapeHtml(String(e.stop.reason).toLowerCase())}` : ''}.`;
	if (e?.program) return `SFO delay program: average ${e.program.avg} minutes, up to ${minutesText(e.program.max)}${e.program.ends ? `, until ${time(e.program.ends)}` : ''}.`;
	return null;
};

export default sfoTickerLine;
